import React, { useState } from 'react';
import { Banknote, CreditCard, PiggyBank, Calendar, Coins, TrendingUp, Users, Shield, Clock, Building } from 'lucide-react';
import { useNavigate, useRoutes } from 'react-router-dom';

const Services = () => {
    const [activeCategory, setActiveCategory] = useState('accounts');
    const navigate = useNavigate()

    const accountServices = [
        {
            icon: CreditCard,
            title: 'Saving Account',
            description: 'Flexible loan account solutions with competitive interest rates and easy repayment options.',
            features: ['Instant Account Opening', 'Exciting Offers & Discounts', 'Secure Internet & Mob Banking', 'Earn Reward Points'],
            color: 'blue',
            onclick: () => { window.open('https://fastwpdemo.com/newwp/finbank/get-in-touch', '_blank') }
        },
        {
            icon: Calendar,
            title: 'Recurring Account',
            description: 'A recurring deposit means making regular deposits.',
            features: ['Monthly Deposits', 'Flexible Tenure', 'Attractive Interest', 'Auto Debit Facility'],
            color: 'green',
            onclick: () => { navigate('/deposit/recurring-account') }

        },
        {
            icon: Coins,
            title: 'Daily Deposit Account',
            description: 'Save small amounts daily and watch your money grow with our daily deposit scheme.',
            features: ['Daily Collection', 'Door-to-Door Service', 'Flexible Amount', 'Regular Returns'],
            color: 'yellow',
            onclick: () => { navigate('/deposit/daily-deposit') }


        },
        {
            icon: Shield,
            title: 'Money Back Account',
            description: 'Get periodic returns on your investment while keeping your principal amount secure.',
            features: ['Periodic Returns', 'Principal Security', 'Flexible Withdrawal', 'Bonus Benefits'],
            color: 'purple',
            onclick: () => { navigate('/deposit/money-back-account') }
        },
        {
            icon: PiggyBank,
            title: 'Fixed Deposit',
            description: 'Secure your future with our fixed deposit schemes offering guaranteed returns.',
            features: ['Guaranteed Returns', 'Multiple Tenure Options', 'Loan Against FD', 'Compound Interest'],
            color: 'indigo',
            onclick: () => { navigate('/deposit/fixed-deposit') }
        },
        {
            icon: TrendingUp,
            title: 'MIS Deposit',
            description: 'Monthly Income Scheme providing regular monthly income with capital appreciation.',
            features: ['Monthly Income', 'Capital Growth', 'Tax Benefits', 'Flexible Investment'],
            color: 'red',
            onclick: () => { navigate('/deposit/mis-deposit') }

        }
    ];

    const loanServices = [
        {
            icon: Banknote,
            title: 'Personal Loan',
            description: 'Quick and hassle-free personal loans for all your financial needs with minimal documentation.',
            features: ['Instant Approval', 'Minimal Documentation', 'Competitive Rates', 'Flexible Repayment'],
            color: 'blue',
            onclick: () => { navigate('/loan/personal-loan') }


        },
        {
            icon: Clock,
            title: 'Daily Loan',
            description: 'Short-term daily loan solutions for immediate financial requirements with quick processing.',
            features: ['Same Day Approval', 'Daily Collection', 'No Collateral', 'Quick Processing'],
            color: 'green',
            onclick: () => { navigate('/loan/daily-loan') }



        },
        {
            icon: Users,
            title: 'Group Loan',
            description: 'Collective lending solutions for groups with shared responsibility and better terms.',
            features: ['Group Guarantee', 'Lower Interest', 'Shared Responsibility', 'Community Support'],
            color: 'orange',
            onclick: () => { navigate('/loan/group-loan') }


        },
        {
            icon: Building,
            title: 'Monthly Secure Loan',
            description: 'Secured monthly loan options with collateral backing for larger amounts and better rates.',
            features: ['Collateral Based', 'Higher Loan Amount', 'Lower Interest', 'Monthly EMI'],
            color: 'purple',
            onclick: () => { navigate('/loan/monthly-secure-loan') }
        }
    ];

    const getColorClasses = (color: string) => {
        const colorMap = {
            blue: 'bg-blue-100 text-blue-600 group-hover:bg-blue-200',
            green: 'bg-green-100 text-green-600 group-hover:bg-green-200',
            yellow: 'bg-yellow-100 text-yellow-600 group-hover:bg-yellow-200',
            purple: 'bg-purple-100 text-purple-600 group-hover:bg-purple-200',
            indigo: 'bg-indigo-100 text-indigo-600 group-hover:bg-indigo-200',
            red: 'bg-red-100 text-red-600 group-hover:bg-red-200',
            orange: 'bg-orange-100 text-orange-600 group-hover:bg-orange-200'
        };
        return colorMap[color as keyof typeof colorMap] || colorMap.blue;
    };

    const currentServices = activeCategory === 'accounts' ? accountServices : loanServices;

    return (
        <section id="services" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                        Our Services
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Comprehensive financial solutions designed to meet all your banking and lending needs
                    </p>
                </div>

                {/* Category Tabs */}
                <div className="flex justify-center mb-12">
                    <div className="bg-white p-2 rounded-2xl shadow-lg border border-gray-200">
                        <button
                            onClick={() => setActiveCategory('accounts')}
                            className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${activeCategory === 'accounts'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                        >
                            Account Services
                        </button>
                        <button
                            onClick={() => setActiveCategory('loans')}
                            className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${activeCategory === 'loans'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                        >
                            Loan Services
                        </button>
                    </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {currentServices.map((service, index) => {
                        const IconComponent = service.icon;
                        return (
                            <div
                                key={index}
                                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group border border-gray-100"
                            >
                                <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-colors duration-300 ${getColorClasses(service.color)}`}>
                                    <IconComponent className="h-8 w-8" />
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-3">
                                    {service.title}
                                </h3>

                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    {service.description}
                                </p>

                                <div className="space-y-2">
                                    {service.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-center space-x-2">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                            <span className="text-sm text-gray-600">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                <button className="mt-6 text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200 flex items-center group" onClick={() => { service.onclick() }}>
                                    Learn More
                                    <TrendingUp className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* CTA Section */}
                <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
                    <h3 className="text-2xl font-bold mb-4">
                        {activeCategory === 'accounts' ? 'Ready to Start Saving?' : 'Need Financial Assistance?'}
                    </h3>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                        {activeCategory === 'accounts'
                            ? 'Open an account with us today and start your journey towards financial growth and security.'
                            : 'Get quick approval on loans with competitive interest rates and flexible repayment options.'
                        }
                    </p>
                    <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200">
                        {activeCategory === 'accounts' ? 'Open Account' : 'Apply for Loan'}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Services;